import { Link, useLocation } from "react-router-dom";
import { Phone, Star, ShieldCheck, Clock, Sparkles, HeartHandshake, ChevronDown, Calculator, Key, ArrowRight } from "lucide-react";
import { AreasSection } from "../components/AreasSection";
import { useScrollReveal, useHashScroll } from "../lib/hooks";
import { SectionBadge } from "../components/Shared";
import { PhotoReportSection } from "../components/PhotoReportSection";
import {
  PHONE, PHONE_LINK, HERO_IMG, SERVICES, FONT_HEAD, FONT_BODY, NAVY, GOLD,
} from "../lib/constants";

const SERVICIOS_ES = {
  residential: {
    title: "Casas y Residencias",
    description: "Limpieza regular y profunda adaptada a tu hogar. Tratamos cada espacio con cuidado y precisión.",
    link: "/es/services/limpieza-de-casas-naples-fl",
  },
  housekeeping: {
    title: "Housekeeping",
    description: "Limpieza semanal, quincenal o mensual con reporte de fotos en cada visita — tu casa cuidada con un horario fijo.",
    link: "/es/services/limpieza-de-casas-naples-fl",
  },
  airbnb: {
    title: "Airbnb y Rentas Vacacionales",
    description: "Cambios rápidos entre huéspedes, siempre lista para 5 estrellas.",
    link: "/es/services/limpieza-airbnb-naples-fl",
  },
  restaurants: {
    title: "Restaurantes",
    description: "Cocinas y comedores impecables que cumplen con los estándares de salubridad.",
    link: "/es/services/limpieza-comercial-naples-fl",
  },
  offices: {
    title: "Oficinas y Comercios",
    description: "Espacios de trabajo limpios que causan la mejor impresión a clientes y empleados.",
    link: "/es/services/limpieza-comercial-naples-fl",
  },
  government: {
    title: "Gobierno y Municipal",
    description: "Contratos públicos con estándares definidos, seguros y certificados disponibles.",
    link: "/government-contracts",
  },
};

const WHY_ES = [
  { Icon: ShieldCheck, t: "Con Licencia y Seguro", d: "Empresa registrada en Florida con seguro de responsabilidad civil. Tu propiedad siempre está protegida." },
  { Icon: Clock, t: "Puntualidad Garantizada", d: "Llegamos a la hora acordada, siempre. Tu tiempo vale y lo respetamos." },
  { Icon: Sparkles, t: "Detalle Obsesivo", d: "De los zócalos a los ventiladores de techo — nuestra lista de verificación no deja nada sin limpiar." },
  { Icon: HeartHandshake, t: "Trato Cercano", d: "Somos un negocio local de Naples. Hablamos español e inglés y respondemos en menos de 24 horas." },
  { Icon: Key, t: "Más de 6 años limpiando Airbnb y casas de renta", d: "Más de 50 propietarios atendidos en Naples, Marco Island y Bonita Springs." },
];

const RESENAS_ES = [
  { name: "Coco Ruiz", role: "Reseña de Google ★5.0", text: "Muy buen servicio, puntualidad y eficiencia. Lo recomiendo 100%." },
  { name: "Jimmy Suarez", role: "Reseña de Google ★5.0", text: "Son muy profesionales y eficientes; hacen un trabajo excelente y brindan un gran servicio. Estoy muy contento con su trabajo y los recomiendo." },
  { name: "Abundi LLC", role: "Reseña de Google ★5.0", text: "Excelente servicio. Fueron puntuales, detallistas y dejaron todo impecable. Muy profesionales y confiables. Muy recomendados." },
];

const FAQS_ES = [
  {
    q: "¿Cuánto cuesta la limpieza de casas en Naples, FL?",
    a: "La mayoría de las limpiezas de casas en Naples, FL cuestan entre $120 y $300+ según el tamaño, la condición y la frecuencia del servicio. La limpieza recurrente (semanal o quincenal) cuesta menos por visita que una limpieza profunda única. Pide tu presupuesto gratis en 60 segundos o llámanos al (239) 244-1934.",
  },
  {
    q: "¿Hacen limpieza de Airbnb y rentas vacacionales?",
    a: "Sí — la limpieza de Airbnb entre huéspedes es una de nuestras especialidades en Naples y el condado de Collier. Nos encargamos de sábanas, reposición de artículos y presentación para que tu propiedad siempre reciba reseñas de 5 estrellas. Hay servicio el mismo día.",
  },
  {
    q: "¿Tienen licencia y seguro?",
    a: "Por supuesto. Cindyrella Cleaning LLC es una empresa de limpieza con licencia y totalmente asegurada en el estado de Florida. Tu casa o negocio está protegido en cada visita.",
  },
  {
    q: "¿Qué zonas atienden?",
    a: "Atendemos Naples, FL y todo el condado de Collier: North Naples, East Naples, Golden Gate, Marco Island y Bonita Springs. Si estás cerca, contáctanos — es muy posible que también podamos ayudarte.",
  },
  {
    q: "¿Cómo pido un presupuesto gratis?",
    a: "Es muy fácil: usa nuestra calculadora de presupuestos en línea para tener un precio en 60 segundos, llámanos o envíanos un mensaje de texto al (239) 244-1934, o completa el formulario de contacto. Respondemos todas las solicitudes en menos de 24 horas.",
  },
];

const HeroEs = () => (
  <section className="relative pt-28 md:pt-36 pb-16 md:pb-24" style={{ background: "#F9FAFB" }} data-testid="es-hero">
    <div className="max-w-7xl mx-auto px-4 md:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
      <div>
        <span className="inline-block px-3 py-1.5 mb-5 text-xs font-bold tracking-widest uppercase" style={{ background: "rgba(212,168,67,0.14)", color: "#b8922a", fontFamily: FONT_HEAD }}>
          Con Licencia y Seguro · Naples, FL
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-tight mb-6" style={{ fontFamily: FONT_HEAD, color: NAVY }} data-testid="es-hero-headline">
          Limpieza de Casas y <span style={{ color: GOLD }}>Housekeeping</span> en Naples, FL
          <br />
          <span className="text-3xl sm:text-4xl" style={{ color: "#2D2D2D" }}>— Servicio de Limpieza con Licencia y Seguro</span>
        </h1>
        <p className="text-base md:text-lg mb-8 max-w-xl" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>
          Casas, Airbnb, restaurantes y oficinas. Presupuestos gratis, respuesta en menos de 24 horas y un estándar simple: dejar cada espacio mejor de como lo encontramos.
        </p>
        <div className="flex flex-wrap gap-4">
          <a href={PHONE_LINK} data-testid="es-hero-call-btn" className="inline-flex items-center gap-2 px-7 py-4 text-base font-bold transition-all hover:opacity-90 hover:shadow-lg" style={{ background: NAVY, color: "#FFFFFF", fontFamily: FONT_BODY }}>
            <Phone size={18} />
            Llámanos: {PHONE}
          </a>
          <Link to="/es/estimate" data-testid="es-hero-estimate-btn" className="inline-flex items-center gap-2 px-7 py-4 text-base font-bold border-2 transition-all hover:bg-[#1B3A6B] hover:text-white" style={{ borderColor: NAVY, color: NAVY, fontFamily: FONT_BODY }}>
            <Calculator size={18} />
            Cotización gratis en 60s
          </Link>
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img src={HERO_IMG} alt="Servicio profesional de limpieza de casas en Naples FL — Cindyrella Cleaning LLC" className="w-full h-[480px] object-cover shadow-2xl" />
      </div>
    </div>
  </section>
);

const ServiciosEs = () => {
  const ref = useScrollReveal();
  return (
    <section ref={ref} id="servicios" className="py-20 md:py-28 section-hidden bg-white" data-testid="es-services">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <SectionBadge>Nuestros Servicios</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
            Limpieza para Cada Espacio
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {SERVICES.map(({ id, image, Icon }) => (
            <div key={id} className="group overflow-hidden bg-white" style={{ border: "1px solid rgba(27,58,107,0.1)" }} data-testid={`es-service-${id}`}>
              <div className="overflow-hidden h-48">
                <img src={image} alt={`${SERVICIOS_ES[id].title} — limpieza profesional en Naples, FL por Cindyrella Cleaning`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Icon size={20} style={{ color: GOLD }} />
                  <h3 className="text-lg font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>{SERVICIOS_ES[id].title}</h3>
                </div>
                <p className="text-sm leading-relaxed" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>{SERVICIOS_ES[id].description}</p>
                {SERVICIOS_ES[id].link && (
                  <Link to={SERVICIOS_ES[id].link} data-testid={`es-service-link-${id}`} className="inline-flex items-center gap-1.5 mt-4 text-sm font-bold transition-colors hover:text-[#b8922a]" style={{ color: GOLD, fontFamily: FONT_BODY }}>
                    Ver más <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const PorQueEs = () => {
  const ref = useScrollReveal();
  return (
    <section ref={ref} className="py-20 md:py-28 section-hidden" style={{ background: NAVY }} data-testid="es-why-us">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <SectionBadge>¿Por Qué Elegirnos?</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ fontFamily: FONT_HEAD }}>
            La Empresa de Limpieza Más Confiable de Naples
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {WHY_ES.map(({ Icon, t, d }) => (
            <div key={t} className="p-6" style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)" }}>
              <Icon size={26} style={{ color: GOLD }} className="mb-4" />
              <h3 className="text-base font-bold text-white mb-2" style={{ fontFamily: FONT_HEAD }}>{t}</h3>
              <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,0.7)", fontFamily: FONT_BODY }}>{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const ResenasEs = () => {
  const ref = useScrollReveal();
  return (
    <section ref={ref} className="py-20 md:py-28 section-hidden" style={{ background: "#F9FAFB" }} data-testid="es-reviews">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <SectionBadge>Reseñas Reales</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
            Lo Que Dicen Nuestros Clientes
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {RESENAS_ES.map(({ name, role, text }) => (
            <div key={name} className="bg-white p-7" style={{ border: "1px solid rgba(27,58,107,0.1)" }}>
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((n) => <Star key={`star-${n}`} size={15} fill={GOLD} style={{ color: GOLD }} />)}
              </div>
              <p className="text-sm leading-relaxed mb-5" style={{ color: "#4B5563", fontFamily: FONT_BODY }}>"{text}"</p>
              <p className="text-sm font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>{name}</p>
              <p className="text-xs" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>{role}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const FaqEs = () => {
  const ref = useScrollReveal();
  return (
    <section ref={ref} className="py-20 md:py-28 section-hidden bg-white" data-testid="es-faq">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <SectionBadge>Preguntas Frecuentes</SectionBadge>
          <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
            Limpieza de Casas en Naples, FL
          </h2>
        </div>
        <div className="space-y-3">
          {FAQS_ES.map(({ q, a }, idx) => (
            <details key={q} data-testid={`es-faq-item-${idx}`} className="group" style={{ border: "1px solid rgba(27,58,107,0.1)", background: "#F9FAFB" }}>
              <summary className="flex items-center justify-between gap-4 cursor-pointer list-none px-6 py-5 text-base font-bold select-none" style={{ fontFamily: FONT_HEAD, color: NAVY }}>
                {q}
                <ChevronDown size={18} className="flex-shrink-0 transition-transform duration-300 group-open:rotate-180" style={{ color: GOLD }} />
              </summary>
              <p className="px-6 pb-6 text-sm leading-relaxed" style={{ color: "#6B7280", fontFamily: FONT_BODY }}>{a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

const CtaEs = () => (
  <section className="py-20" style={{ background: NAVY }} data-testid="es-cta">
    <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
      <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" style={{ fontFamily: FONT_HEAD }}>
        ¿Listo para un Espacio Impecable?
      </h2>
      <p className="text-base mb-8" style={{ color: "rgba(255,255,255,0.75)", fontFamily: FONT_BODY }}>
        Presupuesto gratis y sin compromiso. Respondemos en menos de 24 horas.
      </p>
      <div className="flex flex-wrap justify-center gap-4">
        <a href={PHONE_LINK} data-testid="es-cta-call-btn" className="inline-flex items-center gap-2 px-7 py-4 text-base font-bold transition-all hover:opacity-90" style={{ background: GOLD, color: NAVY, fontFamily: FONT_BODY }}>
          <Phone size={18} />
          {PHONE}
        </a>
        <Link to="/contact" data-testid="es-cta-quote-btn" className="inline-flex items-center gap-2 px-7 py-4 text-base font-bold border-2 text-white transition-all hover:bg-white hover:text-[#1B3A6B]" style={{ borderColor: "#FFFFFF", fontFamily: FONT_BODY }}>
          Pedir Presupuesto Gratis
        </Link>
      </div>
    </div>
  </section>
);

export default function HomeEsPage() {
  useHashScroll(useLocation().hash);
  return (
    <div data-testid="home-es-page">
      <HeroEs />
      <ServiciosEs />
      <PhotoReportSection lang="es" background="#F9FAFB" testid="es-photo-report" />
      <AreasSection lang="es" />
      <PorQueEs />
      <ResenasEs />
      <FaqEs />
      <CtaEs />
    </div>
  );
}
